// Per-run cost and timing report for a tailoring run. The edge function
// returns each Claude call's token usage as _usage on every stage reply; the
// tailoring page collects them here. Measurement only.

export interface CallUsage {
  label: string;
  model: string;
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  ms: number;
}

export interface StageUsage {
  stage: string;        // e.g. "stage1-p1", "stage9"
  seconds: number;      // wall time the page waited for this stage reply
  calls: CallUsage[];
}

// US$ per million tokens. Cache reads bill at 10% of input, cache writes at
// 125% (5-minute cache). Update here if Anthropic's prices change.
const PRICES: Record<string, { input: number; output: number }> = {
  "claude-sonnet-4-6": { input: 3, output: 15 },
  "claude-haiku-4-5": { input: 1, output: 5 },
};

export function callCost(c: CallUsage): number | null {
  const p = PRICES[c.model];
  if (!p) return null; // unknown model: report it, never guess a price
  return (c.input * p.input + c.cacheWrite * p.input * 1.25 + c.cacheRead * p.input * 0.1 + c.output * p.output) / 1_000_000;
}

export interface RunReport {
  at: string;
  totalSeconds: number;
  totalCost: number;
  unpricedModels: string[];
  tokens: { input: number; output: number; cacheRead: number; cacheWrite: number };
  stages: Array<{ stage: string; seconds: number; calls: number; input: number; output: number; cost: number }>;
}

export function buildRunReport(stages: StageUsage[], totalSeconds: number): RunReport {
  const unpriced = new Set<string>();
  const tokens = { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 };
  let totalCost = 0;
  const rows = stages.map((s) => {
    let cost = 0, input = 0, output = 0;
    for (const c of s.calls) {
      const k = callCost(c);
      if (k === null) unpriced.add(c.model); else cost += k;
      input += c.input; output += c.output;
      tokens.input += c.input; tokens.output += c.output;
      tokens.cacheRead += c.cacheRead; tokens.cacheWrite += c.cacheWrite;
    }
    totalCost += cost;
    return { stage: s.stage, seconds: Math.round(s.seconds * 10) / 10, calls: s.calls.length, input, output, cost: Math.round(cost * 10000) / 10000 };
  });
  return {
    at: new Date().toISOString(),
    totalSeconds: Math.round(totalSeconds),
    totalCost: Math.round(totalCost * 10000) / 10000,
    unpricedModels: [...unpriced],
    tokens,
    stages: rows,
  };
}

const KEY = "tw_run_reports";

/** Keep the last 20 run reports in this browser (admin inspection). */
export function saveRunReport(r: RunReport): void {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || "[]") as RunReport[];
    list.unshift(r);
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, 20)));
  } catch { /* storage blocked: the console log still has it */ }
}

export function getRunReports(): RunReport[] {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
}
